<template>
<v-app id="index">
  <v-system-bar
    v-if="!connected"
    class="font-weight-bold overline justify-center"
    color="primary darken-2"
    height="22px"
    dark
    fixed
    app
  >
    {{ $t(`app.value.connectedDisconnected.${connected}`) }}
  </v-system-bar>

  <v-app-bar
    height="60px"
    :color="userModule.moduleBarColor"
    :clipped-left="$vuetify.breakpoint.lgAndUp"
    elevate-on-scroll
    dark
    fixed
    app
  >
    <v-app-bar-nav-icon @click="toggleNavigationsDrawerOpened">
      <v-badge
        :value="userModule.controllers.length"
        icon="mdi-menu"
        color="transparent"
        offset-x="7"
        offset-y="23"
        left
      >
        <v-img
          :src="iconSrc"
          max-width="38"
        />
      </v-badge>
    </v-app-bar-nav-icon>

    <div
      class="ml-1 text-truncate"
      style="cursor: pointer"
      @click="toggleModulesMenuOpened"
    >
      <v-toolbar-title>{{ $t(userModule.title) }}</v-toolbar-title>
      <span>{{ userModule.groupName }}</span>
    </div>

    <v-menu
      v-if="userModules.length > 1"
      v-model="modulesMenuOpened"
      :max-width="menuMaxWidth"
      :max-height="menuMaxHeight"
      left
      offset-y
      scrollable
    >
      <template #activator="{ on }">
        <v-btn
          class="ml-n1"
          icon
          v-on="on"
        >
          <v-icon>{{ modulesMenuOpened ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </template>
      <v-list
        width="360px"
        :max-width="menuMaxWidth"
      >
        <v-subheader class="text-uppercase">
          {{ $t('auth.module.pluralTitle') }}
        </v-subheader>

        <v-list-item-group
          :value="userModules.indexOf(userModule)"
          :mandatory="userModule.moduleName !== 'app'"
        >
          <template v-for="(availableUserModule, i) in userModules">
            <v-divider
              v-if="isModulesMenuDivider(i)"
              :key="`modules-menu-divider-${i}`"
              class="ma-0"
            />

            <v-list-item
              :key="`modules-menu-item-${i}`"
              :to="availableUserModule.routePath"
              color="primary"
            >
              <v-list-item-avatar>
                <v-icon v-if="availableUserModule === userModule">
                  mdi-check
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ $t(availableUserModule.title) }}</v-list-item-title>
                <v-list-item-subtitle>{{ availableUserModule.groupName }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list-item-group>
      </v-list>
    </v-menu>

    <v-spacer/>

    <v-menu
      v-model="notificationsMenuOpened"
      :max-width="menuMaxWidth"
      :max-height="menuMaxHeight"
      offset-y
      scrollable
    >
      <template #activator="{ on }">
        <v-btn
          icon
          v-on="on"
        >
          <v-badge
            color="red"
            :value="notifications.length"
            overlap
          >
            <template #badge>
              <span>{{ notifications.length }}</span>
            </template>
            <v-icon>mdi-bell</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <v-list
        width="390px"
        :max-width="menuMaxWidth"
      >
        <v-btn
          v-if="notifications.length > 1"
          class="mt-2 mr-2 float-right"
          icon
          @click.stop="clearNotifications"
        >
          <v-icon size="23px">
            mdi-close-circle-multiple
          </v-icon>
        </v-btn>

        <v-subheader class="text-uppercase">
          {{ $t('app.index.field.notifications') }}
        </v-subheader>

        <template v-if="notifications.length">
          <v-list-item
            v-for="notification in notifications"
            :key="notification.id"
            :href="notification.clickAction"
            :color="notification.color"
            @click="removeNotification(notification)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ $t(notification.title) }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                v-if="notification.clickAction"
                class="mr-n2 ml-2 text-none"
                :href="notification.clickAction"
                depressed
                @click="removeNotification(notification)"
              >
                {{ $t(notification.clickTitle) }}
              </v-btn>
            </v-list-item-action>
            <v-list-item-action>
              <v-btn
                class="mr-n2"
                icon
                @click.stop="removeNotification(notification)"
              >
                <v-icon size="22px">
                  mdi-close-circle
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
        <v-list-item v-else>
          <v-list-item-content>
            <v-list-item-subtitle class="text-body-2 text-center">
              {{ $t('app.alert.empty') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-menu
      v-model="configurationsMenuOpened"
      :max-width="menuMaxWidth"
      :max-height="menuMaxHeight"
      offset-y
      scrollable
    >
      <template #activator="{ on }">
        <v-btn
          icon
          v-on="on"
        >
          <v-avatar size="32px">
            <v-progress-circular
              v-if="exiting"
              size="26"
              indeterminate
            />
            <v-icon v-else>
              {{ userDetails.email ? 'mdi-account-circle' : 'mdi-dots-vertical' }}
            </v-icon>
          </v-avatar>
        </v-btn>
      </template>
      <v-list
        width="360px"
        :max-width="menuMaxWidth"
      >
        <template v-if="userDetails.email">
          <v-subheader class="text-uppercase">
            {{ $t('auth.user.title') }}
          </v-subheader>

          <v-list-item>
            <v-list-item-avatar>
              <v-icon>mdi-account-circle</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ userDetails.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ userDetails.email }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="exit">
            <v-list-item-avatar>
              <v-icon>mdi-exit-to-app</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('app.button.exit') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('auth.login.button.goto') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="exiting">
              <v-progress-circular
                color="primary"
                indeterminate
              />
            </v-list-item-action>
          </v-list-item>
        </template>

        <v-subheader class="text-uppercase">
          {{ $t('app.index.field.settings') }}
        </v-subheader>

        <v-list-item @click="setThemeDark(!themeDark)">
          <v-list-item-avatar>
            <v-icon>mdi-brightness-4</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ $t('app.index.field.themeDark') }}</v-list-item-title>
            <v-list-item-subtitle>{{ $t(`app.value.enabledDisabled.${themeDark}`) }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-switch
              color="primary"
              :input-value="themeDark"
              dense
              @click.stop="setThemeDark(!themeDark)"
            />
          </v-list-item-action>
        </v-list-item>

        <v-divider
          v-if="configurationsLanguagesMenuOpened"
          class="ma-0"
        />

        <v-list-group
          v-model="configurationsLanguagesMenuOpened"
          @click.stop
        >
          <template #activator>
            <v-list-item-avatar>
              <v-icon>mdi-translate</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('app.index.field.language') }}</v-list-item-title>
              <v-list-item-subtitle v-if="!configurationsLanguagesMenuOpened">
                {{ getLanguageTitle(language) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
          <v-list-item-group
            :value="languages.indexOf(language)"
            mandatory
          >
            <v-list-item
              v-for="supportedLanguage in languages"
              :key="supportedLanguage"
              color="primary"
              @click="setLanguage(supportedLanguage)"
            >
              <v-list-item-avatar>
                <v-icon v-if="supportedLanguage === language">
                  mdi-check
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-subtitle>{{ getLanguageTitle(supportedLanguage) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list-group>

        <v-divider
          v-if="configurationsLanguagesMenuOpened"
          class="ma-0"
        />

        <v-subheader class="text-uppercase">
          {{ $t('app.index.field.about') }}
        </v-subheader>

        <v-list-item>
          <v-list-item-avatar>
            <v-icon>mdi-monitor-cellphone</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ $t('app.index.field.client', { title }) }}</v-list-item-title>
            <v-list-item-subtitle>{{ version }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="serverVersion">
          <v-list-item-avatar>
            <v-icon>mdi-server-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>{{ $t('app.index.field.server', { title }) }}</v-list-item-title>
            <v-list-item-subtitle>{{ serverVersion }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>

  <v-navigation-drawer
    v-if="userModule.controllers.length"
    v-model="navigationsDrawerOpened"
    width="300px"
    :clipped="$vuetify.breakpoint.lgAndUp"
    touchless
    fixed
    app
  >
    <v-subheader
      v-if="!$vuetify.breakpoint.lgAndUp"
      class="text-uppercase"
      @click="autocloseNavigationsDrawer"
    >
      {{ $t('app.index.field.menu') }}
    </v-subheader>

    <v-list-item
      v-for="(controller, i) in userModule.controllers"
      :key="i"
      color="primary"
      :to="`${controller.routePath}${controller.requestDefaultSubpath}`"
      @click="autocloseNavigationsDrawer"
    >
      <v-list-item-content>
        <v-list-item-title>{{ $t(controller.title) }}</v-list-item-title>
      </v-list-item-content>
    </v-list-item>
  </v-navigation-drawer>

  <v-main>
    <v-container fluid>
      <v-slide-y-transition mode="out-in">
        <router-view :key="$route.path"/>
      </v-slide-y-transition>
    </v-container>
  </v-main>

  <v-snackbar
    v-for="(showingNotification, i) in showingNotifications"
    :id="`v-snack-${i}`"
    :key="showingNotification.id"
    :color="showingNotification.color"
    :light="(!showingNotification.color) && themeDark"
    width="390px"
    :max-width="menuMaxWidth"
    timeout="7000"
    value="true"
    top
    right
    @input="autoremoveNotification(showingNotification)"
  >
    <v-list-item class="pa-0">
      <v-list-item-content class="pa-0">
        <v-list-item-title>{{ $t(showingNotification.title) }}</v-list-item-title>
      </v-list-item-content>
    </v-list-item>
    <template #action>
      <v-btn
        v-if="showingNotification.clickAction"
        class="mr-2 ml-2 text-none"
        :href="showingNotification.clickAction"
        :color="showingNotification.color"
        depressed
        @click="removeNotification(showingNotification)"
      >
        {{ $t(showingNotification.clickTitle) }}
      </v-btn>
      <v-btn
        icon
        @click="removeNotification(showingNotification)"
      >
        <v-icon size="22px">
          mdi-close-circle
        </v-icon>
      </v-btn>
    </template>
  </v-snackbar>
</v-app>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { BaseNotification, BaseUserDetails, BaseUserModule } from './types'
import { appStore } from './modules/app/store'
import { authStore } from './modules/auth/store'
import appManifest from '~/public/manifest.json'

@Component
export default class BaseIndexVue extends Vue {
  @Prop({ default: '' })
  source!: string

  modulesMenuOpened: boolean | null = null
  notificationsMenuOpened: boolean | null = null
  configurationsMenuOpened: boolean | null = null
  configurationsLanguagesMenuOpened: boolean | null = null
  navigationsDrawerOpened: boolean | null = null
  showingNotifications: BaseNotification[] = []
  exiting = false

  private lastNotificationId = 0

  get language(): string {
    return appStore.language
  }

  get themeDark(): boolean {
    return appStore.themeDark
  }

  get notifications(): BaseNotification[] {
    return appStore.notifications
  }

  get serverVersion(): string {
    return appStore.serverVersion
  }

  get userDetails(): BaseUserDetails {
    return authStore.userDetails
  }

  get userModules(): BaseUserModule[] {
    return authStore.userModules.slice(1)
  }

  get userModule(): BaseUserModule {
    return authStore.userModule
  }

  get languages(): string[] {
    return appManifest.supported_languages
      .map((supportedLanguage) => supportedLanguage.code)
  }

  get iconSrc(): string {
    return appManifest.icons
      .slice(-1)[0]
      .src
  }

  get title(): string {
    return process.env.VUE_APP_TITLE
  }

  get version(): string {
    return process.env.VUE_APP_VERSION
  }

  get connected(): boolean {
    return true
  }

  get menuMaxWidth(): number {
    return this.$vuetify.breakpoint.width - 24
  }

  get menuMaxHeight(): number {
    const { top, bar, footer } = this.$vuetify.application

    return this.$vuetify.breakpoint.height - (top + bar + footer)
  }

  setLanguage(language: string): void {
    appStore.setLanguage(language)
  }

  getLanguageTitle(language: string): string {
    return appManifest.supported_languages
      .find((supportedLanguage) => supportedLanguage.code === language)
      ?.name ?? ''
  }

  setThemeDark(themeDark: boolean): void {
    appStore.setThemeDark(themeDark)
  }

  async removeNotification(notification: BaseNotification): Promise<void> {
    return appStore.removeNotification(notification)
  }

  async autoremoveNotification(notification: BaseNotification): Promise<void> {
    if (notification.autoremove) return this.removeNotification(notification)
  }

  async clearNotifications(): Promise<void> {
    return appStore.clearNotifications()
  }

  async exit(): Promise<void> {
    if (this.exiting) return

    this.exiting = true

    try {
      await this.$router.push({ name: 'auth.login', query: { exited: 'true' } })
      await authStore.logout()

      this.$notify({ title: 'auth.login.notification.exited' })
    }
    catch (error: any) {
      this.$error(error)
    }

    this.exiting = false
  }

  isModulesMenuDivider(index: number): boolean {
    if ((index < 1) || (index >= this.userModules.length)) return false

    const userModule = this.userModules[index]
    const nextUserModule = this.userModules[index + 1]
    const priorUserModule = this.userModules[index - 1]
    const prepriorUserModule = this.userModules[index - 2]

    return ((userModule.moduleName !== priorUserModule?.moduleName) &&
      ((userModule.moduleName === nextUserModule?.moduleName) ||
      (priorUserModule?.moduleName === prepriorUserModule?.moduleName)))
  }

  async toggleModulesMenuOpened(): Promise<void> {
    if (this.userModules.length > 1) this.modulesMenuOpened = !this.modulesMenuOpened
    else {
      try {
        await this.$router.push({ name: 'app.home' })
      }
      catch (error: any) {
        this.$error(error)
      }
    }
  }

  toggleNavigationsDrawerOpened(): void {
    if (this.userModule.controllers.length) this.navigationsDrawerOpened = !this.navigationsDrawerOpened
  }

  autocloseNavigationsDrawer(): void {
    if ((!this.$vuetify.breakpoint.lgAndUp) && this.navigationsDrawerOpened) this.toggleNavigationsDrawerOpened()
  }

  private fixSnackbarsTop(): void {
    const { top, bar } = this.$vuetify.application

    let priorSnackbar: HTMLElement | null = null
    let priorSnackbarWrapper: HTMLElement | null = null
    let snackbarStyleTop = top + bar

    document.querySelectorAll<HTMLElement>('.v-snack').forEach((snackbar) => {
      priorSnackbarWrapper = priorSnackbar?.querySelector<HTMLElement>('.v-snack__wrapper') ?? null
      snackbarStyleTop += priorSnackbarWrapper ? (priorSnackbarWrapper.offsetHeight + 12) : 0

      snackbar.style.top = `${snackbarStyleTop}px`

      priorSnackbar = snackbar
    })
  }

  @Watch('notifications')
  private async onChangeNotifications(): Promise<void> {
    const oldNotifications = this.showingNotifications.filter((showingNotification) => this.notifications.includes(showingNotification))
    const newNotifications = this.notifications.filter((notification) => (notification.id ?? 0) > this.lastNotificationId)

    this.showingNotifications = ([] as BaseNotification[]).concat(newNotifications, oldNotifications)
    this.lastNotificationId = this.notifications[0]?.id ?? 0

    await this.$nextTick()
    this.fixSnackbarsTop()
  }

  @Watch('configurationsMenuOpened')
  private onChangeConfigurationsMenuOpened(): void {
    this.configurationsLanguagesMenuOpened = null
  }

  mounted(): void {
    this.onChangeNotifications()
    this.onChangeConfigurationsMenuOpened()
  }
}
</script>

<style>
html {
  overflow: auto !important;
}

.v-snack {
  z-index: 202 !important;
}

.v-snack__wrapper {
  margin: 12px !important;
  min-width: auto !important;
  max-width: 390px !important;
}

.v-card {
  margin-right: auto !important;
  margin-left: auto !important;
}

.v-card__title,
.v-toolbar__title {
  font-size: 1.14rem !important;
  font-weight: 500 !important;
}

.v-card__title,
.v-card__subtitle,
.v-toolbar__title {
  line-height: 1.375rem !important;
}

.v-card__title,
.v-alert__wrapper {
  word-break: break-word !important;
}

.v-card__actions {
  padding: 16px !important;
}

.v-list {
  padding-top: 0 !important;
}

.v-list-item {
  min-height: auto !important;
}

.v-list-item__title,
.v-list-item__subtitle {
  white-space: normal !important;
}

.v-divider {
  margin: 2px 0 !important;
}

.v-dialog {
  margin: 12px !important;
}

.v-menu__content {
  z-index: 203 !important;
}

.v-dialog .v-card.theme--light,
.v-menu__content.theme--light {
  border-top: thin solid rgba(0, 0, 0, 0.12) !important;
}

.v-dialog .v-card.theme--dark,
.v-menu__content.theme--dark {
  border: thin solid rgba(255, 255, 255, 0.12) !important;
}

.v-btn > .v-btn__content > .v-icon.theme--light {
  color: rgba(0, 0, 0, 0.57);
}

.v-text-field--rounded > .v-input__control > .v-input__slot {
  padding: 0 10px 0 12px !important;
}

.v-text-field--rounded > .v-input__control > .v-input__slot > .v-progress-linear {
  margin-top: -1px !important;
}

.v-data-table.theme--light table > thead > tr > td,
.v-data-table.theme--light table > thead > tr > th,
.v-data-table.theme--light table > tbody > tr > td,
.v-data-table.theme--light table > tbody > tr > th,
.v-data-table.theme--light table > tfoot > tr > td,
.v-data-table.theme--light table > tfoot > tr > th {
  padding: 6px 10px !important;
  border-color: #E0E0E0 !important;
}

.v-data-table.theme--dark table > thead > tr > td,
.v-data-table.theme--dark table > thead > tr > th,
.v-data-table.theme--dark table > tbody > tr > td,
.v-data-table.theme--dark table > tbody > tr > th,
.v-data-table.theme--dark table > tfoot > tr > td,
.v-data-table.theme--dark table > tfoot > tr > th {
  padding: 6px 10px !important;
  border-color: #393939 !important;
}

.v-data-table table > thead > tr > td:first-child,
.v-data-table table > thead > tr > th:first-child,
.v-data-table table > tbody > tr > td:first-child,
.v-data-table table > tbody > tr > th:first-child,
.v-data-table table > tfoot > tr > td:first-child,
.v-data-table table > tfoot > tr > th:first-child {
  padding-left: 17px !important;
}

.v-data-table table > thead > tr > td.col--pagination,
.v-data-table table > thead > tr > th.col--pagination,
.v-data-table table > tbody > tr > td.col--pagination,
.v-data-table table > tbody > tr > th.col--pagination,
.v-data-table table > tfoot > tr > td.col--pagination,
.v-data-table table > tfoot > tr > th.col--pagination
.v-data-table table > thead > tr > td.col--actions,
.v-data-table table > thead > tr > th.col--actions,
.v-data-table table > tbody > tr > td.col--actions,
.v-data-table table > tbody > tr > th.col--actions,
.v-data-table table > tfoot > tr > td.col--actions,
.v-data-table table > tfoot > tr > th.col--actions {
  padding: 0 !important;
  position: -webkit-sticky !important;
  position: sticky !important;
  right: 0 !important;
  cursor: default !important;
}

.v-data-table table > thead > tr > td.col--actions,
.v-data-table table > thead > tr > th.col--actions {
  z-index: -1 !important;
}

.v-data-table table > tbody > tr > td.col--actions,
.v-data-table table > tbody > tr > th.col--actions,
.v-data-table table > tfoot > tr > td.col--actions,
.v-data-table table > tfoot > tr > th.col--actions {
  z-index: 1 !important;
}

.v-data-table.row--clickable table > tbody > tr > td,
.v-data-table.row--clickable table > tbody > tr > th {
  cursor: pointer !important;
}

.v-data-table .v-data-table-header > tr,
.v-data-table .v-data-table-header > tr {
  position: -webkit-sticky !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 3 !important;
}

.v-data-table.theme--light .v-data-table-header > tr,
.v-data-table.theme--light .v-data-table-header > tr {
  background-color: #F5F5F5 !important;
}

.v-data-table.theme--dark .v-data-table-header > tr,
.v-data-table.theme--dark .v-data-table-header > tr {
  background-color: #272727 !important;
}

.v-data-table .v-data-table-header > tr > td,
.v-data-table .v-data-table-header > tr > th {
  border-bottom-width: 0 !important;
  font-size: 0.8rem !important;
  white-space: nowrap !important;
}

.v-data-table .v-data-table-header__sort-badge {
  margin-left: 3px !important;
  font-weight: 500 !important;
}

.v-data-table.theme--light .v-data-table__empty-wrapper {
  color: rgba(0, 0, 0, 0.6) !important;
}

.v-data-table.theme--dark .v-data-table__empty-wrapper {
  color: rgba(255, 255, 255, 0.7) !important;
}

@media print {
  * {
    color: #333 !important;
  }

  html {
    font-size: 0.688rem !important;
  }

  .container,
  .v-main {
    padding: 0 !important;
  }

  .v-app-bar,
  .v-menu__content,
  .v-navigation-drawer,
  .v-snack {
    display: none !important;
  }

  .v-card {
    padding: 0 !important;
    box-shadow: none !important;
    page-break-before: always !important;
    page-break-inside: avoid !important;
  }

  .v-icon {
    font-size: 1.063rem !important;
  }

  .v-divider {
    border-color: rgba(0, 0, 0, 0.12) !important;
  }
}
</style>